/* eslint-disable no-console */
import {FC, useState, useRef, useEffect, useContext} from "react";
import { App2NotificationContext, App2NotificationSeverity } from "@/app2/shared/contexts/App2NotificationContext";
import {makeStyles} from "tss-react/mui";
import {Alert, Box, Button, Chip, Typography} from "@mui/material";
import {Add, Delete} from "@mui/icons-material";
import {DataGridPro, GridColDef, GridToolbarContainer, GridToolbarFilterButton} from "@mui/x-data-grid-pro";
import {IconAdminCourses} from "@/icons/IconAdminCourses";
import {useGetCourseModules, useGetCurriculumItems, type CourseResponse} from "@/app2/shared/data";
import {LessonSearchModal} from "./LessonSearchModal";
import {LessonEditorModal} from "./LessonEditorModal";

const MAX_CURRICULUM_ITEMS = 20;

/**
 * Simplified flat list item for curriculum display
 */
export interface IFlatListItem {
	id: string; // Unique drag ID (e.g., "lesson-123")
	moduleId: number; // Actual lesson ID
	title: string;
	slug: string;
	isDraft: boolean;
	order: number; // Position in curriculum
}

export interface IProps {
	courseId?: number;
	onChange: (curriculum: IFlatListItem[]) => void;
	/** Loaded course from improve-api (optional; reserved for future use). */
	initialData?: CourseResponse;
}

const useStyles = makeStyles()((theme) => ({
	container: {
		height: "auto"
	},
	dataGrid: {
		border: "1px solid " + theme.palette.divider,
		"& .MuiDataGrid-columnHeader": {
			backgroundColor: theme.palette.background.default,
			fontWeight: 600
		}
	},
	modulesAddedFooterStat: {
		borderTop: "1px solid " + theme.palette.divider,
		color: "#FFFFFFB2",
		fontSize: "12px",
		padding: theme.spacing(2)
	},
	chooseFromLibBtn: {
		"svg path": {
			fill: "black"
		},
		"svg": {
			width: "18px"
		}
	},
	createNewBtn: {
		color: theme.palette.primary.main,
		"&:hover": {
			color: theme.palette.primary.contrastText
		}
	}
}));

export const AdminCourseCurriculum: FC<IProps> = ({courseId, onChange, initialData: _initialData}) => {
	const {classes} = useStyles();
	const [curriculum, setCurriculum] = useState<IFlatListItem[]>([]);
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);
	const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
	const [isCreatingLesson, setIsCreatingLesson] = useState(false);
	const { notify } = useContext(App2NotificationContext);
	const lastServerCurriculumKey = useRef<string | null>(null);
	const curriculumHydratedRef = useRef(false);

	// Fetch curriculum items and modules for existing course
	const {data: modulesData, isLoading: modulesLoading} = useGetCourseModules(courseId);
	const {data: curriculumItemsData, isLoading: curriculumLoading} = useGetCurriculumItems(courseId);

	useEffect(() => {
		lastServerCurriculumKey.current = null;
		if (courseId === undefined) {
			curriculumHydratedRef.current = true;
		} else {
			curriculumHydratedRef.current = false;
		}
	}, [courseId]);

	useEffect(() => {
		if (courseId === undefined) {
			return;
		}
		if (!curriculumItemsData || !modulesData) {
			return;
		}

		const key = curriculumItemsData.items.map((item) => item.moduleId).join("|");
		if (key === lastServerCurriculumKey.current) {
			return;
		}
		lastServerCurriculumKey.current = key;

		const moduleMap = new Map(modulesData.modules.map((m) => [m.id, m]));
		const flatList: IFlatListItem[] = curriculumItemsData.items.map((item, index) => {
			const module = moduleMap.get(item.moduleId);
			return {
				id: `lesson-${item.moduleId}`,
				moduleId: item.moduleId,
				title: module?.title || `Lesson ${item.moduleId}`,
				slug: module?.slug || '',
				isDraft: module?.publishedAt === null,
				order: index
			};
		});

		setCurriculum(flatList);
		curriculumHydratedRef.current = true;
	}, [courseId, curriculumItemsData, modulesData]);

	// Notify parent of changes
	useEffect(() => {
		if (!curriculumHydratedRef.current) {
			return;
		}
		onChange(curriculum);
	}, [curriculum, courseId]);

	// Handle adding lessons from library (search modal)
	const handleAddFromLibrary = (selectedLessons: Array<{id: number; title: string; slug: string; publishedAt: Date | null}>) => {
		const freeSpaces = MAX_CURRICULUM_ITEMS - curriculum.length;
		
		if (freeSpaces <= 0) {
			notify("Max lesson count has already been reached", "", App2NotificationSeverity.warning, 10000, false);
			return;
		}

		// Filter out lessons already in curriculum
		const newLessons = selectedLessons.filter(
			lesson => !curriculum.some(item => item.moduleId === lesson.id)
		);

		if (newLessons.length > freeSpaces) {
			newLessons.splice(freeSpaces);
			notify(`You can only add ${MAX_CURRICULUM_ITEMS} to the course. Additional lessons were not added.`, "", App2NotificationSeverity.warning, 10000);
		}

		// Add new lessons to curriculum
		const newItems: IFlatListItem[] = newLessons.map(lesson => ({
			id: `lesson-${lesson.id}`,
			moduleId: lesson.id,
			title: lesson.title,
			slug: lesson.slug,
			isDraft: lesson.publishedAt === null,
			order: curriculum.length
		}));

		setCurriculum(prev => [...prev, ...newItems]);
		setIsImportModalOpen(false);
	};

	// Handle creating/editing a lesson
	const handleLessonSaved = (lesson: {id: number; title: string; slug: string; publishedAt: Date | null}) => {
		const existingIndex = curriculum.findIndex(item => item.moduleId === lesson.id);
		
		if (existingIndex >= 0) {
			// Update existing lesson in curriculum
			setCurriculum(prev => {
				const updated = [...prev];
				updated[existingIndex] = {
					...updated[existingIndex],
					title: lesson.title,
					slug: lesson.slug,
					isDraft: lesson.publishedAt === null
				};
				return updated;
			});
		} else {
			// Add new lesson to curriculum
			const newItem: IFlatListItem = {
				id: `lesson-${lesson.id}`,
				moduleId: lesson.id,
				title: lesson.title,
				slug: lesson.slug,
				isDraft: lesson.publishedAt === null,
				order: curriculum.length
			};
			setCurriculum(prev => [...prev, newItem]);
		}

		setIsCreatingLesson(false);
		setEditingLessonId(null);
	};

	// Handle deleting a lesson from curriculum
	const handleDelete = (dragId: string) => {
		setCurriculum(prev => prev.filter(item => item.id !== dragId));
	};

	// Handle lesson deletion in modal
	const handleLessonDeleted = (lessonId: number) => {
		setCurriculum(prev => prev.filter(item => item.moduleId !== lessonId));
		setEditingLessonId(null);
	};

	// DataGrid columns
	const columns: GridColDef<IFlatListItem>[] = [
		{
			field: "title",
			headerName: "Title",
			flex: 1,
			minWidth: 200,
			renderCell: (params) => (<Box
					sx={{cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
					onClick={() => setEditingLessonId(params.row.moduleId)}
				>
					{params.row.title}
				</Box>
			)
		},
		{
			field: "isDraft",
			headerName: "",
			width: 75,
			sortable: false,
			filterable: false,
			renderCell: (params) => params.row.isDraft ? (
				<Chip
					variant="outlined"
					color="primary"
					label="Draft"
					size="small"
					sx={{ padding: "4px" }}
				/>
			) : <></>
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<Delete 
					onClick={(e) => {
						e.stopPropagation();
						handleDelete(params.row.id);
					}}
					sx={{cursor: "pointer"}}
				/>
			)
		}
	];

	const isLoading = modulesLoading || curriculumLoading;
	const canAddMore = curriculum.length < MAX_CURRICULUM_ITEMS;
	const hasUnpublishedLessons = curriculum.some((item) => item.isDraft);

	return (
		<>
			{hasUnpublishedLessons && (
				<Alert severity="warning" sx={{mb: 2}}>
					Some lessons are unpublished (drafts) and will not appear in the course until they are published.
				</Alert>
			)}
			<Box className={classes.container}>
				{curriculum.length > 0 && (
					<DataGridPro<IFlatListItem>
						className={classes.dataGrid}
						autoHeight
						getRowId={(row) => row.id}
						loading={isLoading}
						rows={curriculum}
						columns={columns}
						rowReordering
						onRowOrderChange={(params) => {
							const {oldIndex, targetIndex} = params;
							setCurriculum((prev) => {
								const updated = [...prev];
								const [movedItem] = updated.splice(oldIndex, 1);
								updated.splice(targetIndex, 0, movedItem);
								return updated.map((item, index) => ({...item, order: index}));
							});
						}}
						hideFooter
						components={{
							Toolbar: () => (
								<GridToolbarContainer>
									{/* @ts-expect-error MUI X GridToolbarFilterButton props are over-broad; defaults are valid */}
									<GridToolbarFilterButton />
								</GridToolbarContainer>
							),
							Footer: () => (
								<Box className={classes.modulesAddedFooterStat}>
									<Typography variant="caption">
										{curriculum.length}/{MAX_CURRICULUM_ITEMS} ADDED
									</Typography>
								</Box>
							)
						}}
						disableSelectionOnClick
					/>
				)}

				<Box display="flex" gap={2} mt={2}>
					<Button
						disabled={!canAddMore}
						variant="contained"
						className={classes.chooseFromLibBtn}
						onClick={() => {
							if (!canAddMore) {
								notify("Max lesson count has already been reached", "", App2NotificationSeverity.warning, 10000, false);
							} else {
								setIsImportModalOpen(true);
							}
						}}
					>
						<IconAdminCourses /> CHOOSE FROM LIBRARY
					</Button>
					<Button
						disabled={!canAddMore}
						variant="outlined"
						className={classes.createNewBtn}
						onClick={() => {
							if (!canAddMore) {
								notify("Max lesson count has already been reached", "", App2NotificationSeverity.warning, 10000, false);
							} else {
								setIsCreatingLesson(true);
							}
						}}
					>
						<Add /> CREATE NEW
					</Button>
				</Box>
			</Box>

			{/* Lesson Search/Import Modal */}
			{isImportModalOpen && (
				<LessonSearchModal
					currentLessons={curriculum.map(item => item.moduleId)}
					onClose={() => setIsImportModalOpen(false)}
					onAdd={handleAddFromLibrary}
				/>
			)}

			{/* Lesson Create/Edit Modal */}
			{(isCreatingLesson || editingLessonId !== null) && (
				<LessonEditorModal
					lessonId={editingLessonId || undefined}
					onClose={() => {
						setIsCreatingLesson(false);
						setEditingLessonId(null);
					}}
					onSave={handleLessonSaved}
					onDelete={handleLessonDeleted}
				/>
			)}
		</>
	);
};

export default AdminCourseCurriculum;
